import { useEffect, useMemo, useRef } from 'react';
import type { TechnicianAssignment } from '@/lib/supabase/technicianAssignments';
import { isFinalAssignmentStatus } from '@/lib/supabase/assignmentStatus';
import type { PreviewRecallRef } from '@/lib/supabase/recallLifecycle';

/**
 * Record that a dispatcher opened a route, and repair any lifecycle record whose
 * completion was lost.
 *
 * ── Why this is batched (RLM-06) ──
 *
 * This was one write per recall, issued over EVERY assignment in the tenant, and
 * re-fired on every technician switch and every assignment refetch: 6,164
 * requests for the largest tenant on a single panel open. It is now a single
 * batched pass — see syncLifecycleForRoutePreview.
 *
 * ── The two populations are different, on purpose ──
 *
 * `viewed` is what the route actually shows: one technician, one day, still live
 * work. The old code stamped `viewed_at` on every recall in the tenant, which
 * made the column meaningless.
 *
 * `repairable` stays tenant-wide. The repair only ever acts on FINISHED recalls,
 * so narrowing it to the visible route would silently switch off the backlog
 * correction (RLM-63) — the plan's `visibleAssignments` sketch, which keeps only
 * non-terminal assignments, would have done exactly that.
 *
 * ── Why the effect keys on content, not array identity ──
 *
 * React Query hands back a new array on every refetch even when nothing changed.
 * Keying on identity re-fired this constantly; keying on a sorted signature means
 * a repeat open costs nothing.
 */
export function useRecallViewTracking(input: {
  technicianId: string | undefined;
  /** Stable YYYY-MM-DD for the day on screen. */
  dateKey: string;
  /** What the route shows — already narrowed to technician, day and live work. */
  visibleAssignments: TechnicianAssignment[];
  /** Every assignment in the tenant; only the finished ones are repair candidates. */
  allAssignments: TechnicianAssignment[];
}): void {
  const { technicianId, dateKey, visibleAssignments, allAssignments } = input;

  // Manual stops are not recalls: they borrow the table's identity columns with a
  // synthetic VIN and the literal campaign `MANUAL`, so tracking them records a
  // view against something that has no recall lifecycle.
  const viewed = useMemo(
    () => toRefs(visibleAssignments.filter(a => a.stop_type !== 'manual')),
    [visibleAssignments]
  );

  const repairable = useMemo(
    () => toRefs(allAssignments.filter(a => isFinalAssignmentStatus(a.assignment_status))),
    [allAssignments]
  );

  // Content signatures: a refetch that changes nothing must not re-fire this.
  const viewedSignature = useMemo(() => signatureOf(viewed), [viewed]);
  const repairableSignature = useMemo(() => signatureOf(repairable), [repairable]);

  // Everything already handled this session. A ref rather than state, so marking
  // work as done cannot itself trigger another render or another pass.
  const handled = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!technicianId) return;

    const freshViews = viewed.filter(
      ref => !handled.current.has(`view|${technicianId}|${dateKey}|${refKey(ref)}`)
    );
    const freshRepairs = repairable.filter(ref => !handled.current.has(`repair|${refKey(ref)}`));
    if (freshViews.length === 0 && freshRepairs.length === 0) return;

    // Mark BEFORE awaiting, so an overlapping run cannot duplicate the work.
    freshViews.forEach(ref =>
      handled.current.add(`view|${technicianId}|${dateKey}|${refKey(ref)}`)
    );
    freshRepairs.forEach(ref => handled.current.add(`repair|${refKey(ref)}`));

    void (async () => {
      try {
        const { syncLifecycleForRoutePreview } = await import('@/lib/supabase/recallLifecycle');
        await syncLifecycleForRoutePreview({ viewed: freshViews, repairable: freshRepairs });
      } catch {
        // Non-critical: opening a route must never fail because of tracking.
      }
    })();
    // The arrays are read through their signatures; adding them would re-fire on
    // every refetch, which is the defect this hook exists to fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianId, dateKey, viewedSignature, repairableSignature]);
}

function refKey(ref: PreviewRecallRef): string {
  return `${ref.vin}|${ref.campaignNumber}`;
}

function signatureOf(refs: PreviewRecallRef[]): string {
  return refs
    .map(ref => `${refKey(ref)}:${ref.assignmentStatus ?? ''}`)
    .sort()
    .join(',');
}

/** One entry per recall. The newest assignment wins, which is the order they arrive in. */
function toRefs(assignments: TechnicianAssignment[]): PreviewRecallRef[] {
  const byRecall = new Map<string, PreviewRecallRef>();
  for (const assignment of assignments) {
    if (!assignment.vin || !assignment.campaign_number) continue;
    const key = `${assignment.vin}|${assignment.campaign_number}`;
    if (byRecall.has(key)) continue;
    byRecall.set(key, {
      vin: assignment.vin,
      campaignNumber: assignment.campaign_number,
      assignmentId: assignment.id,
      assignmentStatus: assignment.assignment_status,
      completedAt: assignment.completed_at
    });
  }
  return [...byRecall.values()];
}
